import { useMutation, useQueryClient } from "@tanstack/react-query";
import { privateAxios } from "../axiosInstances";
import { isAxiosError } from "axios";
import { businessSectorQueryKeys } from "../query-keys/business-sector.query-keys";
import { MergeBusinessSectorDto, UpsertBusinessSectorDto } from "@ac-expo/api-types";

const updateBusinessSector = async ({
  id,
  data,
}: {
  id: string;
  data: UpsertBusinessSectorDto;
}) => {
  try {
    const { data: response } = await privateAxios.patch(
      `/business-sector/${id}`,
      data
    );
    return response;
  } catch (error) {
    if (isAxiosError(error))
      error.message = error.response?.data.message ?? error.message;
    throw error;
  }
};

export const useUpdateBusinessSectorMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateBusinessSector,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: businessSectorQueryKeys.all,
      });
    },
  });
};

const deleteBusinessSector = async ({ id }: { id: string }) => {
  try {
    const { data: response } = await privateAxios.delete(
      `/business-sector/${id}`
    );
    return response;
  } catch (error) {
    if (isAxiosError(error))
      error.message = error.response?.data.message ?? error.message;
    throw error;
  }
};

export const useDeleteBusinessSectorMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteBusinessSector,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: businessSectorQueryKeys.all,
      });
    },
  });
};

const mergeBusinessSectors = async ({ data }: { data: MergeBusinessSectorDto }) => {
  try {
    const { data: response } = await privateAxios.post(`/business-sector/merge`, data);
    return response;
  } catch (error) {
    if (isAxiosError(error))
      error.message = error.response?.data.message ?? error.message;
    throw error;
  }
};

export const useMergeBusinessSectorMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: mergeBusinessSectors,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: businessSectorQueryKeys.all,
      });
    },
  });
};

const unmergeBusinessSector = async ({ id }: { id: string }) => {
  try {
    const { data: response } = await privateAxios.post(`/business-sector/${id}/unmerge`);
    return response;
  } catch (error) {
    if (isAxiosError(error))
      error.message = error.response?.data.message ?? error.message;
    throw error;
  }
};

export const useUnmergeBusinessSectorMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: unmergeBusinessSector,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: businessSectorQueryKeys.all,
      });
    },
  });
};
